/**
 * useFileTree Hook
 *
 * Manages file tree state:
 * - Tree data fetching
 * - Selection state
 * - Expansion state
 */

import { useQuery } from "@tanstack/react-query";
import { useState, useCallback, useMemo, useEffect } from "react";

import { configApi } from "../../api/configApi";
import { useAuthStore } from "../../store/authStore";
import { useConfigStore } from "../../store/configStore";
import type { FileTreeNode, FileTreeSelection, ConfigType } from "../../types";

interface UseFileTreeResult {
  readonly tree: readonly FileTreeNode[];
  readonly isLoading: boolean;
  readonly error: string | undefined;
  readonly selectedPath: string | undefined;
  readonly expandedPaths: ReadonlySet<string>;
  readonly selectNode: (path: string) => void;
  readonly toggleExpand: (path: string) => void;
  readonly expandAll: () => void;
  readonly collapseAll: () => void;
}

export function useFileTree(): UseFileTreeResult {
  const { tenantId, environment } = useAuthStore();
  const { setCurrentPath, setMetadata } = useConfigStore();

  const [selection, setSelection] = useState<FileTreeSelection>({
    selectedPath: undefined,
    expandedPaths: new Set<string>(),
  });

  // Fetch config list
  const {
    data: configList,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["configs", tenantId, environment],
    queryFn: () => configApi.listConfigs({ tenantId, environment }),
    enabled: !!tenantId,
    staleTime: 1000 * 60, // 1 minute
  });

  // Build tree structure from flat config list
  const tree = useMemo(() => {
    if (!configList?.success) return [];
    return buildFileTree(configList.data);
  }, [configList]);

  // Select a node
  const selectNode = useCallback(
    (path: string) => {
      setSelection((prev) => ({
        ...prev,
        selectedPath: path,
      }));
      setCurrentPath(path);

      // Load metadata for selected config
      if (configList?.success) {
        const config = configList.data.find((c: ConfigListItem) => c.path === path);
        if (config) {
          setMetadata(config);
        }
      }
    },
    [configList, setCurrentPath, setMetadata]
  );

  // Toggle expansion
  const toggleExpand = useCallback((path: string) => {
    setSelection((prev) => {
      const newExpanded = new Set(prev.expandedPaths);
      if (newExpanded.has(path)) {
        newExpanded.delete(path);
      } else {
        newExpanded.add(path);
      }
      return { ...prev, expandedPaths: newExpanded };
    });
  }, []);

  // Expand all
  const expandAll = useCallback(() => {
    const allPaths = new Set<string>();
    function collectPaths(nodes: readonly FileTreeNode[]): void {
      for (const node of nodes) {
        if (node.type === "folder") {
          allPaths.add(node.path);
          if (node.children) {
            collectPaths(node.children);
          }
        }
      }
    }
    collectPaths(tree);
    setSelection((prev) => ({ ...prev, expandedPaths: allPaths }));
  }, [tree]);

  // Collapse all
  const collapseAll = useCallback(() => {
    setSelection((prev) => ({ ...prev, expandedPaths: new Set() }));
  }, []);

  // Auto-expand first level on load
  useEffect(() => {
    if (tree.length > 0 && selection.expandedPaths.size === 0) {
      const firstLevel = new Set<string>();
      for (const node of tree) {
        if (node.type === "folder") {
          firstLevel.add(node.path);
        }
      }
      setSelection((prev) => ({ ...prev, expandedPaths: firstLevel }));
    }
  }, [tree, selection.expandedPaths.size]);

  return {
    tree,
    isLoading,
    error: error ? String(error) : undefined,
    selectedPath: selection.selectedPath,
    expandedPaths: selection.expandedPaths,
    selectNode,
    toggleExpand,
    expandAll,
    collapseAll,
  };
}

interface ConfigListItem {
  readonly path: string;
  readonly configType: ConfigType;
  readonly verticalKey: string;
  readonly version: number;
  readonly status: "draft" | "live" | "archived";
  readonly checksum: string;
  readonly createdBy: string;
  readonly createdAt: string;
}

function buildFileTree(configs: readonly ConfigListItem[]): readonly FileTreeNode[] {
  const verticalMap = new Map<string, FileTreeNode>();

  for (const config of configs) {
    const verticalKey = config.verticalKey;

    // Get or create vertical folder
    let verticalNode = verticalMap.get(verticalKey);
    if (!verticalNode) {
      verticalNode = {
        id: `vertical-${verticalKey}`,
        name: formatVerticalName(verticalKey),
        type: "folder",
        path: verticalKey,
        children: [],
      };
      verticalMap.set(verticalKey, verticalNode);
    }

    // Add config file
    const configNode: FileTreeNode = {
      id: `config-${config.path}`,
      name: formatConfigName(config.configType),
      type: "file",
      path: config.path,
      configType: config.configType,
      status: config.status,
      isLocked: config.status === "live",
    };

    // TypeScript requires mutable array for push
    (verticalNode.children as FileTreeNode[]).push(configNode);
  }

  // Sort verticals and their children
  const result = Array.from(verticalMap.values()).sort((a, b) => a.name.localeCompare(b.name));

  for (const vertical of result) {
    if (vertical.children) {
      (vertical.children as FileTreeNode[]).sort(
        (a, b) => getConfigTypeOrder(a.configType) - getConfigTypeOrder(b.configType)
      );
    }
  }

  return result;
}

function formatVerticalName(key: string): string {
  return key
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatConfigName(type: ConfigType): string {
  const labels: Record<ConfigType, string> = {
    schema: "Schema",
    eligibility: "Eligibility Rules",
    ranking: "Ranking Config",
    journey: "Journey",
    branding: "Branding",
  };
  return labels[type];
}

function getConfigTypeOrder(type: ConfigType | undefined): number {
  const order: Record<ConfigType, number> = {
    schema: 1,
    eligibility: 2,
    ranking: 3,
    journey: 4,
    branding: 5,
  };
  return type ? order[type] : 99;
}
